import _isString from 'lodash/isString';
import _isEmpty from 'lodash/isEmpty';
import {
  getCoBrand,
  getExperience,
  getQualtricsConfig,
  getUserProfile,
  isBusinessUser,
  isLoggedIn,
  isMobileLayout,
  isTabletLayout,
} from './twHelper';
import { experiences, pageNames } from '../common/constants';
import {
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_DOC_ID_KEY,
} from '../../js/contactUs/constants/ecmFPTITagsKeys';

// eslint-disable-next-line import/prefer-default-export
export const getUserDisplayName = () => {
  const userProfile = getUserProfile();
  if (typeof userProfile === 'object') {
    if (getCoBrand().toLowerCase() === 'jp' && Object.hasOwnProperty.call(userProfile, 'lastName')) {
      return userProfile.lastName;
    }
    if (Object.hasOwnProperty.call(userProfile, 'firstName')) {
      return userProfile.firstName;
    }
    return '';
  }
  return '';
};

export const getUrlWithQueryParams = (url, queryParams) => {
  let finalUrl = `${url}?`;
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(queryParams)) {
    finalUrl = `${finalUrl}${key}=${value}&`;
  }
  return finalUrl;
};

/**
 *  This will set key and value in session
 * @param key
 * @param value
 * @returns {string|null}
 */
export const setInSession = (key, value) => {
  try {
    return window.sessionStorage && sessionStorage.setItem(key, value);
  } catch (e) {
    return null;
  }
};

/*
 * This will get the value by using the key in session
 * @param key
 * @returns {string|null}
 * */
export const getSession = (key) => {
  try {
    return window.sessionStorage && window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const getCurrentOrigin = () => window && window.location && window.location.origin;

export const getCurrentOriginWithCoBrand = () => {
  const currentOrigin = getCurrentOrigin();
  return `${currentOrigin}/${getCoBrand()}`;
};

const cleanTitle = (title) =>
  encodeURIComponent(title && title.replace(/ /g, '-').replace(/\?/g, '').replace(/[/\\]/g, '-').toLowerCase());

export const getFormattedUrlForArticle = (title, id) => decodeURIComponent(`${cleanTitle(title)}-${id.toLowerCase()}`);

export const getArticleRedirectUrl = (title, id) =>
  `${getCurrentOriginWithCoBrand()}/smarthelp/article/${getFormattedUrlForArticle(title, id)}`;

export const getIcfRedirectUrl = (title, id) =>
  `${getCurrentOriginWithCoBrand()}/smarthelp/PAYPAL_HELP_GUIDE/${getFormattedUrlForArticle(title, id)}`;

export const getGuidesUrl = () => `${getCurrentOriginWithCoBrand()}/smarthelp/PAYPAL_HELP_GUIDE`;

export const getSearchUrl = (query) => `${getCurrentOriginWithCoBrand()}/smarthelp/search?q=${query}`;

export const parseSearchQuery = (query) => {
  const returnValue = query ? query.trim() : '';
  try {
    return encodeURIComponent(returnValue);
  } catch (error) {
    return returnValue;
  }
};

export const layoutSize = () => {
  if (isMobileLayout()) {
    return 'sm';
  }
  if (isTabletLayout()) {
    return 'md';
  }
  return 'lg';
};

/**
 * Function to get experience of customer and change the css of the header based on the customer-experience
 */
export const getHeaderStyleByAccountType = () => {
  if (!isLoggedIn()) {
    return 'logout';
  }
  return !isBusinessUser() ? 'personal' : 'business';
};

export const getFPTIPageId = () => {
  try {
    const locationPath = window.location && window.location.pathname;
    if (locationPath && _isString(locationPath)) {
      const pageNamePath = locationPath.split('/'); // Split path by '/'
      if (pageNamePath && Object.values(pageNames).includes(pageNamePath[3])) {
        return pageNamePath[3];
      }
    }
    return pageNames.HOME;
  } catch (err) {
    return pageNames.HOME;
  }
};

export const isPersonalizedComponentEnabled = () => getExperience() !== experiences.TECHNICAL;

export const isBNAComponentEnabled = () => isLoggedIn() && getExperience() !== experiences.TECHNICAL;

/**
 * For enabling Qualtrics Feedback icon
 */
export const isQualtricsFeedbackEnabled = (pageName) => {
  const qualticsConfig = getQualtricsConfig(); // qualtrics config
  if (qualticsConfig && !_isEmpty(qualticsConfig)) {
    const isMarketEnabled = qualticsConfig.isMarketEnabledForQualtrics; // Checking whether market enabled or not
    const pagename = `${pageName}Page`;
    // Checking whether market is enabled and of page names existance from qualtrics config
    if (isMarketEnabled && Array.isArray(qualticsConfig.pagesEnabled) && qualticsConfig.pagesEnabled.length > 0) {
      // Checking whether all the pages are enabled
      const isEnabledAllPages = qualticsConfig.pagesEnabled[0].enableAllPages;
      // Checking whether page is enabled
      const isPageEnabled = qualticsConfig.pagesEnabled[0][pagename];

      return !!(isMarketEnabled && (isEnabledAllPages || isPageEnabled));
    }
  }
  return false;
};

/**
 * Function to set session variable when Customer Journey API is called
 */

export const setCustomerJourneyAPISessionKey = (value) =>
  window.sessionStorage.setItem('customerJourneyAPICalled', value);

/**
 * Function to get session variable of Customer Journey API if already called
 */
export const getCustomerJourneyAPISessionKey = () => window.sessionStorage.getItem('customerJourneyAPICalled');

export const redirectToHomePage = () => {
  window.location.href = `${getCurrentOriginWithCoBrand()}/smarthelp/home`;
};

export const getLocationPath = () => {
  return window && window.location && window.location.pathname;
};

export const getOriginPath = () => `${window.location.protocol}//${window.location.host}`;

// Function to return fptiDocId and fptiComponentIds, from ECM ArticleData, used in Article FPTIs
export const getEcmArticleFptiTags = (articleData) => {
  const fptiDocId = articleData[ECM_DOC_ID_KEY] || ECM_DOC_ID_DEFAULT_VALUE;
  const fptiComponentIds = articleData[ECM_COMPONENT_IDS_KEY] || ECM_COMPONENT_IDS_DEFAULT_VALUE;

  return { fptiDocId, fptiComponentIds };
};
